import * as rxjs from 'rxjs'

import type { PropertiesConfig } from '../config'
import { Feature } from '../feature'
import type { State } from '../state'

import type { LocalStorage } from './local'
import type { SessionStorage } from './session'

/**
 * Abstract {@link Feature} powered by a {@link Storage} instance from the
 * {@link https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API Web Storage API}
 *
 * @see {@link LocalStorage}, {@link SessionStorage}
 */
export abstract class WebStorage<
  // NOTE: this must NOT have any default! If it does, it won't get inferred,
  // which breaks a lot
  const TPropertiesConfig extends PropertiesConfig.Constraint,
> extends Feature<TPropertiesConfig> {
  /**
   * {@link Storage} that powers this {@link WebStorage}
   */
  public abstract readonly storage: Storage

  /**
   * @see {@link _getFullKey}
   */
  private get _fullKeyPrefix(): string {
    return `${this.id}:`
  }

  constructor(params: WebStorage.constructor.Params<TPropertiesConfig>) {
    super({
      ...params,
      initialValues: rxjs.defer(
        // NOTE: we are just hoping the current values match the declared
        // properties
        () => rxjs.of(this._getAllValues() as State.Values<TPropertiesConfig>),
      ),
    })
  }
  /**
   * Writes a single value to {@link storage}
   */
  protected onWriteProperty: Feature<TPropertiesConfig>['onWriteProperty'] = (
    forwardTo,
    request,
  ) => {
    // Writes the value
    const fullKey = this._getFullKey(request.entry.key)
    if (request.entry.value === undefined) {
      this.storage.removeItem(fullKey)
    } else {
      this.storage.setItem(fullKey, JSON.stringify(request.entry.value))
    }
    // And we are done!
    return rxjs.of({ success: true })
  }

  /**
   * Writes all values to {@link storage}
   */
  protected onWriteAllProperties: Feature<TPropertiesConfig>['onWriteAllProperties'] =
    (forwardTo, request) => {
      // First we need to remove any values corresponding to this feature
      this.clearStoredValues()
      // Then we write the new values
      for (const key in request.values) {
        const fullKey = this._getFullKey(key)
        const value = request.values[key]
        if (value === undefined) {
          this.storage.removeItem(fullKey)
        } else {
          this.storage.setItem(fullKey, JSON.stringify(value))
        }
      }
      // And we are done!
      return rxjs.of({ success: true })
    }

  /**
   * To avoid collisions between multiple instances of this class, we use
   * {@link _fullKeyPrefix} with the given partial key to produce a full key
   * that won't collide with other instances
   */
  private _getFullKey(partialKey: string): string {
    return `${this._fullKeyPrefix}${partialKey}`
  }

  /** Removes every browser-storage entry owned by this feature. */
  protected clearStoredValues(): void {
    // Removing keys changes Storage indexes, so collect them before deletion.
    const fullKeys = [...this._listFullKeys()]
    for (const key of fullKeys) {
      this.storage.removeItem(key)
    }
  }

  /**
   * Lists the current full keys in {@link storage}
   */
  private *_listFullKeys(): Generator<string> {
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i)
      if (key?.startsWith(this._fullKeyPrefix)) {
        yield key
      }
    }
  }

  /**
   * Retrieves all current values for this {@link WebStorage} in the state
   */
  private _getAllValues(): Record<string, unknown> {
    const values: Record<string, unknown> = {}
    for (const key of this._listFullKeys()) {
      const valueJson = this.storage.getItem(key)
      if (valueJson) {
        values[key.replace(this._fullKeyPrefix, '')] = JSON.parse(valueJson)
      }
    }
    return values
  }
}
export namespace WebStorage {
  export namespace constructor {
    export type Params<TPropertiesConfig extends PropertiesConfig.Constraint> =
      Omit<Feature.constructor.Params<TPropertiesConfig>, 'initialValues'>
  }
}
